import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Avatar,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ref, get, child, update } from 'firebase/database';
import { database } from '../../firebase';

const ProfilePic = ({ open, handleClose }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ name: '', phone: '' });
  const [newProfilePic, setNewProfilePic] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!storedUser) {
        setError("User not logged in.");
        setLoading(false);
        return;
      }
      try {
        const dbRef = ref(database);
        const snapshot = await get(child(dbRef, 'users'));
        if (snapshot.exists()) {
          const usersData = snapshot.val();
          // Use Object.entries to get the key along with the data
          const userEntry = Object.entries(usersData).find(([key, value]) =>
            value.email &&
            value.email.toLowerCase() === storedUser.email.toLowerCase()
          );
          if (userEntry) {
            const [userKey, userProfile] = userEntry;
            // Save the node key in the profile data (_key)
            setProfileData({ ...userProfile, _key: userKey });
          } else {
            setError("Profile not found.");
          }
        } else {
          setError("No users found.");
        }
      } catch (err) {
        setError("Failed to load profile data.");
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      setLoading(true);
      fetchProfileData();
      setEditMode(false);
      setNewProfilePic(null);
    }
  }, [open]);

  // Populate editData when profileData is loaded
  useEffect(() => {
    if (profileData) {
      const fullName = `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim();
      setEditData({
        name: fullName,
        phone: profileData.phone || '',
      });
    }
  }, [profileData]);

  const handleEditChange = (e) => {
    setEditData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Use FileReader to generate a data URL for preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProfilePic(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setUpdateLoading(true);
    try {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!storedUser) {
        setError("User not logged in.");
        setUpdateLoading(false);
        return;
      }
      // Split full name into first and last (simple split)
      const parts = editData.name.trim().split(' ');
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ') || '';
      const updatedData = {
        first_name: firstName,
        last_name: lastName,
        phone: editData.phone,
      };
      // If a new photo was uploaded, include it in the update.
      if (newProfilePic) {
        updatedData.profilePic = newProfilePic;
      }
      // Use the node key from the fetched profile data
      const userKey = profileData._key;
      const dbRef = ref(database, `users/${userKey}`);
      await update(dbRef, updatedData);
      // Merge the updated fields with the existing data
      setProfileData((prev) => ({ ...prev, ...updatedData }));
      setEditMode(false);
      setError('');
    } catch (err) {
      setError("Failed to save changes. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const userName = profileData ? `${profileData.first_name} ${profileData.last_name}`.trim() : 'Profile';
  const profileImage = newProfilePic || (profileData?.profilePic) || "https://via.placeholder.com/150";

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {userName}'s Profile
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              src={profileImage}
              alt={userName}
              sx={{ width: 200, height: 200, margin: 'auto', mb: 2 }}
            />
            {editMode && (
              <>
                <Button
                  variant="contained"
                  onClick={() => fileInputRef.current.click()}
                  sx={{ mb: 2 }}
                >
                  Upload/Take Photo
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
              </>
            )}
            {!editMode ? (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {userName}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Phone: {profileData.phone || 'N/A'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Email: {profileData.email || 'N/A'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organization: {profileData.organization || 'N/A'}
                </Typography>
                <Button variant="outlined" onClick={() => setEditMode(true)}>
                  Edit
                </Button>
              </>
            ) : (
              <>
                <TextField
                  name="name"
                  label="Full Name"
                  value={editData.name}
                  onChange={handleEditChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  name="phone"
                  label="Phone"
                  value={editData.phone}
                  onChange={handleEditChange}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email"
                  value={profileData.email || ''}
                  fullWidth
                  disabled
                  sx={{ mb: 2, backgroundColor: "#f9f9f9", borderRadius: "4px" }}
                />
                <TextField
                  label="Organization"
                  value={profileData.organization || ''}
                  fullWidth
                  disabled
                  sx={{ mb: 2, backgroundColor: "#f9f9f9", borderRadius: "4px" }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={updateLoading}
                    sx={{ width: '48%' }}
                  >
                    {updateLoading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setEditMode(false);
                      setNewProfilePic(null);
                    }}
                    sx={{ width: '48%' }}
                  >
                    Cancel
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfilePic;
